import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VpError, WhitelabelConfig as GQLWhitelabelConfig } from '../../../generated/internal.graphql';
import * as VPError from '../../../VPError';
import { type InternalApiError, toApiErrors } from '../errors';
import type { VPResult } from '../../VPResult';
import * as ExpectedDnsHost from './ExpectedDnsHost';
import * as OnboardingStatus from './OnboardingStatus';
import * as StoredOnboardingStatus from './StoredOnboardingStatus';
import * as WhitelabelConfig from './WhitelabelConfig';
import {
  INTERNAL_WHITELABEL_CLOUDFLARE_HOSTNAME_FIELDS,
  INTERNAL_WHITELABEL_CONFIG_FIELDS,
  INTERNAL_WHITELABEL_DNS_RECORD_CHECK_FIELDS,
  INTERNAL_WHITELABEL_ONBOARDING_STATUS_FIELDS,
} from './fragments';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';

/**
 * Whitelabel configuration administration for VenuePilot staff — the per-domain brand registry.
 *
 * ADR-009 moved this surface off the **legacy** schema (`apiClient`, `generated/graphql`, nested
 * under `app { … }`) directly onto the internal one, never passing through AppSchema: the backend
 * type is a `Types::SuperadminObject`, so it must not sit in a schema offered to account holders.
 * Auth is the superadmin Rails session cookie — a bearer token never satisfies the gate, and it only
 * works in the Rails-embedded admin against a same-origin `/graphql`, never the standalone dev
 * server — so everything here goes through `internalGqlApiClient`.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo *throws*
 * rather than returning a payload. Every function below therefore catches and classifies via
 * `toApiErrors`, so callers can branch on `isUnauthorized` and say "VenuePilot staff only" instead of
 * rendering an empty table — and so an expected authorization outcome does not reach Sentry as an
 * exception. **Nothing here throws.**
 *
 * Two result shapes, matching `Internal/AppConfig/Api.ts`: the reads resolve `VPResult`, the
 * mutations resolve `MutationResult`, except the re-check, which resolves `VPResult` like a read.
 * `saveWhitelabelConfig`'s payload carries its entity, so it needs no follow-up refetch;
 * `deleteWhitelabelConfig` carries only `success` and `errors`.
 */

export type MutationResult = { success: boolean; errors: InternalApiError[] };

const INTERNAL_WHITELABEL_CONFIGS_QUERY = gql`
  query InternalWhitelabelConfigs {
    whitelabelConfigs {
      ...InternalWhitelabelConfigFields
    }
  }
  ${INTERNAL_WHITELABEL_CONFIG_FIELDS}
`;

const INTERNAL_WHITELABEL_ONBOARDING_STATUS_QUERY = gql`
  query InternalWhitelabelOnboardingStatus($id: Int!) {
    whitelabelOnboardingStatus(id: $id) {
      ...InternalWhitelabelOnboardingStatusFields
    }
  }
  ${INTERNAL_WHITELABEL_ONBOARDING_STATUS_FIELDS}
`;

// Kept out of `INTERNAL_WHITELABEL_CONFIG_FIELDS`: a backend that predates the field rejects the whole
// document, and that fragment also carries the list, the save payload and the account brand picker.
const INTERNAL_WHITELABEL_EXPECTED_DNS_RECORDS_QUERY = gql`
  query InternalWhitelabelExpectedDnsRecords {
    whitelabelConfigs {
      id
      expectedDnsRecords {
        role
        host
        error
        records {
          role
          name
          type
          target
          dnsOnly
          purpose
        }
      }
    }
  }
`;

type ExpectedDnsRecordsQuery = {
  whitelabelConfigs?: Pick<GQLWhitelabelConfig, 'id' | 'expectedDnsRecords'>[] | null;
};

const INTERNAL_SAVE_WHITELABEL_CONFIG_MUTATION = gql`
  mutation InternalSaveWhitelabelConfig($config: WhitelabelAttributes!) {
    saveWhitelabelConfig(input: { config: $config }) {
      config {
        ...InternalWhitelabelConfigFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_WHITELABEL_CONFIG_FIELDS}
`;

const INTERNAL_DELETE_WHITELABEL_CONFIG_MUTATION = gql`
  mutation InternalDeleteWhitelabelConfig($id: Int!) {
    deleteWhitelabelConfig(input: { id: $id }) {
      success
      errors {
        message
        path
      }
    }
  }
`;

const INTERNAL_RECHECK_WHITELABEL_ONBOARDING_MUTATION = gql`
  mutation InternalRecheckWhitelabelOnboarding($id: Int!) {
    recheckWhitelabelOnboarding(input: { id: $id }) {
      status {
        configId
        checkedAt
        cloudflareHostnames {
          ...InternalWhitelabelCloudflareHostnameFields
        }
        dnsRecords {
          ...InternalWhitelabelDnsRecordCheckFields
        }
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_WHITELABEL_CLOUDFLARE_HOSTNAME_FIELDS}
  ${INTERNAL_WHITELABEL_DNS_RECORD_CHECK_FIELDS}
`;

const toErrors = (errors?: VpError[] | null): InternalApiError[] =>
  VPError.fromGqlCollection(errors ?? []);

/**
 * A failed mutation with an empty `errors` list leaves the caller nothing to render. Today's
 * resolvers always populate one — but a null payload does not, and neither would a future
 * `success: false` with no message, so fall back rather than report a silent failure.
 */
const failed = (errors: InternalApiError[], fallback: string): MutationResult => ({
  success: false,
  errors: errors.length ? errors : [{ path: '', message: fallback }],
});

export const fetchWhitelabelConfigs = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<WhitelabelConfig.WhitelabelConfigType[], InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_WHITELABEL_CONFIGS_QUERY,
      // Account-agnostic: the registry is keyed by domain, not by account — checkout resolves the
      // same row by hostname (`checkout/CheckoutBranding.ts`).
      context: ACCOUNT_AGNOSTIC,
      fetchPolicy: 'network-only',
    });

    // `whitelabelConfigs` is nullable. An absent list is a backend fault, not an empty one —
    // resolving it as one would render "no whitelabel configurations" and hide the failure.
    if (!data?.whitelabelConfigs) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch the whitelabel configurations.' }] };
    }

    return { success: true, data: WhitelabelConfig.fromGqlCollection(data.whitelabelConfigs) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/** Reads the last stored check, calling neither Cloudflare nor DNS; `null` means never checked. */
export const fetchWhitelabelOnboardingStatus = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<OnboardingStatus.OnboardingStatus | null, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_WHITELABEL_ONBOARDING_STATUS_QUERY,
      // Account-agnostic: same registry as the list read, addressed by config id.
      context: ACCOUNT_AGNOSTIC,
      variables: { id },
      // This replaces the runbook's own `curl` checks, which a cached answer could contradict.
      fetchPolicy: 'network-only',
    });

    if (data?.whitelabelOnboardingStatus === null) {
      return { success: true, data: null };
    }

    if (!data?.whitelabelOnboardingStatus) {
      return { success: false, errors: [{ path: '', message: 'Failed to read the onboarding status.' }] };
    }

    return { success: true, data: OnboardingStatus.fromGql(data.whitelabelOnboardingStatus) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/** Runs the onboarding check and stores it; the payload carries no overall state, only the layers. */
export const recheckWhitelabelOnboarding = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<StoredOnboardingStatus.StoredOnboardingStatus, InternalApiError[]>> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_RECHECK_WHITELABEL_ONBOARDING_MUTATION,
      context: ACCOUNT_AGNOSTIC,
      variables: { id },
      fetchPolicy: 'network-only',
    });

    const errors = toErrors(data?.recheckWhitelabelOnboarding?.errors);
    const stored = data?.recheckWhitelabelOnboarding?.status;

    if (errors.length || !stored) {
      return { success: false, errors: failed(errors, 'Failed to re-check the onboarding status.').errors };
    }

    return { success: true, data: StoredOnboardingStatus.fromGql(stored) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/** Every config's expected records in one read: there is no singular config query to narrow it to. */
export const fetchWhitelabelExpectedDnsRecords = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<Record<number, ExpectedDnsHost.ExpectedDnsHost[]>, InternalApiError[]>> => {
  try {
    const { data } = await client.query<ExpectedDnsRecordsQuery>({
      query: INTERNAL_WHITELABEL_EXPECTED_DNS_RECORDS_QUERY,
      context: ACCOUNT_AGNOSTIC,
      fetchPolicy: 'network-only',
    });

    if (!data?.whitelabelConfigs) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch the expected DNS records.' }] };
    }

    return {
      success: true,
      data: Object.fromEntries(
        data.whitelabelConfigs.map(config => [config.id, ExpectedDnsHost.fromGqlCollection(config.expectedDnsRecords)]),
      ),
    };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const saveWhitelabelConfig = async (
  client: ApolloClient<NormalizedCacheObject>,
  config: WhitelabelConfig.WhitelabelConfigInput,
): Promise<MutationResult & { config?: WhitelabelConfig.WhitelabelConfigType }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SAVE_WHITELABEL_CONFIG_MUTATION,
      // Account-agnostic: same registry, and a created domain belongs to no account either.
      context: ACCOUNT_AGNOSTIC,
      variables: { config },
      fetchPolicy: 'network-only',
    });

    const errors = toErrors(data?.saveWhitelabelConfig?.errors);
    const saved = data?.saveWhitelabelConfig?.config;

    if (errors.length || !saved) {
      return failed(errors, 'Failed to save the whitelabel configuration.');
    }

    return { success: true, errors: [], config: WhitelabelConfig.fromGql(saved) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const deleteWhitelabelConfig = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<MutationResult> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_DELETE_WHITELABEL_CONFIG_MUTATION,
      // Account-agnostic: same registry, addressed by config id.
      context: ACCOUNT_AGNOSTIC,
      variables: { id },
      fetchPolicy: 'network-only',
    });

    if (!data?.deleteWhitelabelConfig?.success) {
      return failed(toErrors(data?.deleteWhitelabelConfig?.errors), 'Failed to delete the whitelabel configuration.');
    }

    return { success: true, errors: [] };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
